// SCR-CAT-01 Products list and SCR-CAT-06 categories and brands (frozen mockups-cat-v1, ADR-087, ADR-088).
// Owner: tabs Products · Categories · Brands. Storekeeper: the list only, no prices, no costs.
// P10 (mockups-adv-v1): New family and New bundle (owner, ADR-122), a Family filter, variant, bundle and lot rows.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Filters,
  FormGrid,
  List,
  ListRow,
  Notice,
  SearchField,
  Select,
  SkeletonList,
  Tabs,
  Chips,
  VirtualList,
} from '../../design/kit';
import { loadBarcodes, type Brand, type Category, type Product } from '../../data/catalog';
import { Code, Name } from '../../i18n/Isolate';
import { formatCount } from '../../lib/format';
import type { Language } from '../../i18n';
import { useSession } from '../../app/session';
import { BrandSheet, CategorySheet, ExportSheet, FindBarcodeSheet, ProductSheet } from './ProductSheets';
import { BundleSheet, FamilySheet, VariantSheet, useBundleAvailability, useFamilies } from './AdvancedSheets';
import { CATALOG, refusalCode, useCatalogData, useMoney, usePhotoLinks, useTab } from './common';
import { messageKey } from '../../lib/errors';

const TABS = ['products', 'categories', 'brands'] as const;
type Status = 'active' | 'inactive' | 'all';
type Sheet =
  | { kind: 'product' }
  | { kind: 'family' }
  | { kind: 'variant'; family: string }
  | { kind: 'bundle' }
  | { kind: 'export' }
  | { kind: 'find' }
  | { kind: 'category'; category: Category | null }
  | { kind: 'brand'; brand: Brand | null }
  | null;

export function ProductsPage() {
  const { t } = useTranslation();
  const { role } = useSession();
  const owner = role === 'owner';
  const [tab, setTab] = useTab(TABS, 'products');
  const data = useCatalogData();
  const [sheet, setSheet] = useState<Sheet>(null);
  const close = () => setSheet(null);
  const navigate = useNavigate();
  const barcodes = useQuery({
    queryKey: [...CATALOG, 'barcodes'],
    queryFn: () => loadBarcodes(),
    enabled: sheet?.kind === 'export',
  });
  const shown = owner ? tab : 'products';
  const families = useFamilies();

  if (data.products.isError) return <Notice tone="danger">{t(messageKey(refusalCode(data.products.error)))}</Notice>;
  return (
    <>
      {owner && (
        <Tabs
          label={t('p.products')}
          value={tab}
          onChange={(id) => setTab(id as (typeof TABS)[number])}
          items={TABS.map((id) => ({ id, label: t(`tab.${id}`) }))}
        />
      )}
      {shown === 'products' && <ProductsTab onSheet={setSheet} />}
      {shown === 'categories' && <CategoriesTab categories={data.categories.data} onSheet={setSheet} />}
      {shown === 'brands' && <BrandsTab brands={data.brands.data} onSheet={setSheet} />}

      {sheet?.kind === 'product' && (
        <ProductSheet
          product={null}
          price={undefined}
          categories={data.categories.data ?? []}
          brands={data.brands.data ?? []}
          families={families.data ?? []}
          onFamily={(family) => setSheet({ kind: 'variant', family })}
          onClose={close}
          onSaved={(id) => {
            close();
            if (id) navigate(`/products/${id}`);
          }}
        />
      )}
      {sheet?.kind === 'family' && (
        <FamilySheet
          categories={data.categories.data ?? []}
          brands={data.brands.data ?? []}
          onClose={close}
          onSaved={(family) => setSheet({ kind: 'variant', family })}
        />
      )}
      {sheet?.kind === 'variant' && families.data && (
        <VariantSheet
          families={families.data}
          family={sheet.family}
          onClose={close}
          onSaved={(id) => {
            close();
            navigate(`/products/${id}`);
          }}
        />
      )}
      {sheet?.kind === 'bundle' && (
        <BundleSheet
          products={data.products.data ?? []}
          onClose={close}
          onSaved={(id) => {
            close();
            navigate(`/products/${id}`);
          }}
        />
      )}
      {sheet?.kind === 'category' && (
        <CategorySheet category={sheet.category} categories={data.categories.data ?? []} onClose={close} />
      )}
      {sheet?.kind === 'brand' && <BrandSheet brand={sheet.brand} onClose={close} />}
      {sheet?.kind === 'find' && (
        <FindBarcodeSheet
          onClose={close}
          onFound={(id) => {
            close();
            navigate(`/products/${id}`);
          }}
        />
      )}
      {sheet?.kind === 'export' && (
        <ExportSheet
          products={data.products.data ?? []}
          prices={data.prices.data ?? new Map()}
          costs={data.costs.data ?? new Map()}
          barcodes={(barcodes.data ?? []).reduce(
            (m, b) => m.set(b.product_id, [...(m.get(b.product_id) ?? []), b.code]),
            new Map<string, string[]>(),
          )}
          onClose={close}
        />
      )}
    </>
  );
}

function ProductsTab({ onSheet }: { onSheet: (s: Sheet) => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const { role } = useSession();
  const owner = role === 'owner';
  const navigate = useNavigate();
  const money = useMoney();
  const { products, categories, brands, prices, costs } = useCatalogData();
  const photos = usePhotoLinks((products.data ?? []).map((p) => p.image_path));
  const [text, setText] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [family, setFamily] = useState('');
  const families = useFamilies();
  const availability = useBundleAvailability();
  const [status, setStatus] = useState<Status>('active');
  const [belowReorder, setBelowReorder] = useState(false);

  const list = useMemo(() => {
    const q = text.trim().toLowerCase();
    // Filtering by a category includes its subcategories (PRD-06): match the path prefix.
    const catPath = categories.data?.find((c) => c.id === category)?.path;
    return (products.data ?? []).filter(
      (p) =>
        (status === 'all' || p.active === (status === 'active')) &&
        (!q || p.name.toLowerCase().includes(q) || (p.sku ?? '').toLowerCase().includes(q)) &&
        (!catPath || (p.category_path ?? '') === catPath || (p.category_path ?? '').startsWith(`${catPath} › `)) &&
        (!brand || p.brand_id === brand) &&
        (!family || p.family_id === family) &&
        // Stock arrives with P2: until then every product's stock is zero.
        (!belowReorder || p.reorder_level > 0),
    );
  }, [products.data, categories.data, text, category, brand, family, status, belowReorder]);
  const filtered = Boolean(text.trim() || category || brand || family || status !== 'active' || belowReorder);
  const clear = () => {
    setText('');
    setCategory('');
    setBrand('');
    setFamily('');
    setStatus('active');
    setBelowReorder(false);
  };

  return (
    <>
      {owner && (
        <div className="yv-chips">
          <Button pill variant="main" icon="plus" onClick={() => onSheet({ kind: 'product' })}>
            {t('newproduct')}
          </Button>
          <Button pill icon="layers" onClick={() => onSheet({ kind: 'family' })}>
            {t('newfamily')}
          </Button>
          <Button pill icon="box" onClick={() => onSheet({ kind: 'bundle' })}>
            {t('newbundle')}
          </Button>
          <Button pill icon="download" onClick={() => onSheet({ kind: 'export' })}>
            {t('export')}
          </Button>
        </div>
      )}
      <Filters>
        <SearchField
          value={text}
          onChange={setText}
          label={t('search.products')}
          onScan={() => onSheet({ kind: 'find' })}
          scanLabel={t('scan')}
        />
        <FormGrid>
          {(families.data ?? []).length > 0 && (
            <Field label={t('f.family')}>
              {({ id }) => (
                <Select id={id} value={family} onChange={(e) => setFamily(e.target.value)}>
                  <option value="">{t('f.allfams')}</option>
                  {(families.data ?? []).map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <Field label={t('f.category')}>
            {({ id }) => (
              <Select id={id} value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">{t('f.allcats')}</option>
                {(categories.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.path}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('f.brand')}>
            {({ id }) => (
              <Select id={id} value={brand} onChange={(e) => setBrand(e.target.value)}>
                <option value="">{t('f.allbrands')}</option>
                {(brands.data ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </FormGrid>
        <Chips
          label={t('status')}
          items={[
            { id: 'active', label: t('f.active') },
            { id: 'inactive', label: t('f.inactive') },
            { id: 'all', label: t('f.all') },
            { id: 'reorder', label: t('f.reorder') },
          ]}
          selected={[status, ...(belowReorder ? ['reorder'] : [])]}
          onToggle={(id) => (id === 'reorder' ? setBelowReorder((v) => !v) : setStatus(id as Status))}
        />
      </Filters>
      {!products.data ? (
        <SkeletonList label={t('loading')} />
      ) : products.data.length === 0 ? (
        <EmptyState
          icon="tag"
          title={t('empty.products')}
          text={t('empty.products.text')}
          action={
            owner ? (
              <Button variant="main" icon="plus" onClick={() => onSheet({ kind: 'product' })}>
                {t('newproduct')}
              </Button>
            ) : undefined
          }
        />
      ) : list.length === 0 ? (
        <EmptyState
          icon="search"
          title={t('nomatch')}
          text={t('nomatch.text')}
          action={filtered ? <Button onClick={clear}>{t('clearfilters')}</Button> : undefined}
        />
      ) : (
        <VirtualList
          label={t('p.products')}
          items={list}
          rowKey={(p) => p.id}
          row={(p) => (
            <ProductRow
              product={p}
              photo={p.image_path ? photos?.get(p.image_path) : undefined}
              price={prices.data?.get(p.id)}
              cost={owner ? costs.data?.get(p.id)?.avg_cost : undefined}
              available={
                p.is_bundle
                  ? availability.data?.find((a) => a.bundle_id === p.id && a.kind === 'garage')?.available
                  : undefined
              }
              money={money}
              count={(n: number) => formatCount(n, lang)}
              onOpen={() => navigate(`/products/${p.id}`)}
            />
          )}
        />
      )}
    </>
  );
}

function ProductRow({
  product: p,
  photo,
  price,
  cost,
  available,
  money,
  count,
  onOpen,
}: {
  product: Product;
  /** PRD-08: bundles available from the garage's components. */
  available?: number | undefined;
  /** PRD-12: the thumbnail's link, when the product has a photo. */
  photo: string | undefined;
  price: { piece_price: number | null; box_price: number | null } | undefined;
  cost: number | null | undefined;
  money: (v: number | null | undefined) => string;
  count: (n: number) => string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  // The selling price per piece, or per box for box-only products (PRD-11).
  const value = price ? (p.sell_piece ? price.piece_price : price.box_price) : null;
  return (
    <ListRow
      testId={`product-${p.sku ?? p.id}`}
      lead={photo ? { image: photo } : { icon: p.is_bundle ? 'box' : 'tag' }}
      title={<Name>{p.name}</Name>}
      onOpen={onOpen}
      sub={
        <>
          {p.sku && (
            <>
              <Code>{p.sku}</Code> ·{' '}
            </>
          )}
          {p.category_path && (
            <>
              <Name>{p.category_path}</Name> ·{' '}
            </>
          )}
          {p.is_bundle
            ? (p.components ?? []).map((c, i) => (
                <span key={c.product_id}>
                  {i > 0 && ' + '}
                  <Name>{c.name}</Name> ×{c.quantity}
                </span>
              ))
            : p.box_capacity
              ? t('boxof', { n: count(p.box_capacity) })
              : t('loose')}
          {p.family_name && (
            <>
              <br />
              {t('var.of', { f: '' })}
              <Name>{p.family_name}</Name>
            </>
          )}
          {p.is_bundle && available !== undefined && (
            <>
              <br />
              {t('bun.availn', { n: count(available) })}
            </>
          )}
          {cost !== undefined && cost !== null && (
            <>
              <br />
              {t('avgcost')} {money(cost)}
            </>
          )}
        </>
      }
      value={value !== null ? money(value) : undefined}
      badge={
        <>
          {value !== null && <span className="yv-row__sub">{t(p.sell_piece ? 'unit.piece' : 'unit.box')}</span>}
          {!p.active && <Badge>{t('inactive')}</Badge>}
          {p.is_bundle && <Badge tone="info">{t('bun.badge')}</Badge>}
          {p.lot_tracked && <Badge>{t('lots.badge')}</Badge>}
          {p.expired_lots > 0 && <Badge tone="danger">{t('lots.expbadge')}</Badge>}
        </>
      }
    />
  );
}

function CategoriesTab({ categories, onSheet }: { categories: Category[] | undefined; onSheet: (s: Sheet) => void }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="yv-chips">
        <Button pill variant="main" icon="plus" onClick={() => onSheet({ kind: 'category', category: null })}>
          {t('newcategory')}
        </Button>
      </div>
      {!categories ? (
        <SkeletonList label={t('loading')} />
      ) : categories.length === 0 ? (
        <EmptyState icon="folder" title={t('tab.categories')} text={t('c6.desc')} />
      ) : (
        <List label={t('tab.categories')}>
          {categories.map((c) => (
            <ListRow
              key={c.id}
              testId={`category-${c.name}`}
              depth={Math.min(c.depth, 3) as 0 | 1 | 2 | 3}
              lead={c.depth === 0 ? { icon: 'folder' } : undefined}
              title={<Name>{c.name}</Name>}
              value={t('products.n', { n: c.product_count })}
              actions={
                <Button pill variant="quiet" onClick={() => onSheet({ kind: 'category', category: c })}>
                  {t('rename')}
                </Button>
              }
            />
          ))}
        </List>
      )}
    </>
  );
}

function BrandsTab({ brands, onSheet }: { brands: Brand[] | undefined; onSheet: (s: Sheet) => void }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="yv-chips">
        <Button pill variant="main" icon="plus" onClick={() => onSheet({ kind: 'brand', brand: null })}>
          {t('newbrand')}
        </Button>
      </div>
      {!brands ? (
        <SkeletonList label={t('loading')} />
      ) : (
        <List label={t('tab.brands')}>
          {brands.map((b) => (
            <ListRow
              key={b.id}
              testId={`brand-${b.name}`}
              lead={{ icon: 'tag' }}
              title={<Name>{b.name}</Name>}
              value={t('products.n', { n: b.product_count })}
              actions={
                <Button pill variant="quiet" onClick={() => onSheet({ kind: 'brand', brand: b })}>
                  {t('rename')}
                </Button>
              }
            />
          ))}
        </List>
      )}
    </>
  );
}
